import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import MathRenderer from '../components/MathRenderer';
import { ArrowLeft, Target, AlertTriangle, CheckCircle, XCircle, Lightbulb, Play, BookOpen, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#E63946', '#FF6B35', '#FFD23F', '#06D6A0', '#1B4F8A', '#8338EC'];

export default function WeaknessDetector() {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [practicing, setPracticing] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [showPrereqs, setShowPrereqs] = useState(false);

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await fetch(`${API}/student/weakness-analysis`, { headers: getHeaders() });
      setAnalysis(await res.json());
    } catch { toast.error('Failed to load analysis'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const startWeaknessPractice = async () => {
    try {
      const res = await fetch(`${API}/student/weakness-practice?limit=10`, { headers: getHeaders() });
      const data = await res.json();
      if (!data.questions?.length) { toast.error('No questions available. Try practicing first!'); return; }
      setQuestions(data.questions);
      setCurrentIdx(0);
      setAnswer('');
      setFeedback(null);
      setScore({ correct: 0, total: 0 });
      setPracticing(true);
    } catch { toast.error('Failed to load'); }
  };

  const checkAnswer = async () => {
    const q = questions[currentIdx];
    const qType = q.question_type || 'mcq';
    let isCorrect = false;
    if (qType === 'mcq' || qType === 'assertion_reason') isCorrect = answer.toUpperCase() === (q.correct_option || '').toUpperCase();
    else if (qType === 'true_false') isCorrect = answer.toLowerCase() === (q.correct_answer || '').toLowerCase();
    else if (['fill_blank','integer_type'].includes(qType)) isCorrect = String(answer).trim().toLowerCase() === String(q.correct_answer || '').trim().toLowerCase();
    setFeedback({ is_correct: isCorrect, correct_answer: q.correct_option || q.correct_answer, solution: q.solution });
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), total: s.total + 1 }));
    // Save answer
    try {
      await fetch(`${API}/practice/deep/answer`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ question_id: q.id, answer, session_id: 'weakness_practice' }),
      });
    } catch {}
  };

  const nextQ = () => {
    if (currentIdx >= questions.length - 1) {
      toast.success(`Practice complete! ${score.correct}/${score.total}`);
      setPracticing(false);
      fetchAnalysis();
      return;
    }
    setCurrentIdx(i => i + 1);
    setAnswer('');
    setFeedback(null);
  };

  const q = questions[currentIdx];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="space-y-3 w-full max-w-md px-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-24" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => practicing ? setPracticing(false) : navigate('/home')} data-testid="weakness-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
        </button>
        <Target className="w-5 h-5 text-[#E63946]" />
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">
          {practicing ? 'Weakness Practice' : 'Improve Weak Areas'}
        </h1>
        {practicing && <span className="ml-auto text-sm font-bold text-[#FF6B35]">{score.correct}/{score.total}</span>}
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-4">
        {!practicing ? (
          <>
            {/* Summary */}
            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-4 text-center">
                <p className="text-sm font-bold text-slate-500 mb-1">Questions Analyzed</p>
                <p className="text-3xl font-black text-[#1B4F8A]">{analysis?.total_answers || 0}</p>
              </CardContent>
            </Card>

            {/* Weak Areas */}
            {(analysis?.weak_chapters?.length > 0) ? (
              <Card className="border-2 border-red-200 rounded-2xl mb-4">
                <CardContent className="p-4">
                  <h3 className="font-bold text-red-500 text-sm mb-3 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Weak Chapters (below 60% accuracy)
                  </h3>
                  <ResponsiveContainer width="100%" height={Math.max(150, (analysis?.weak_chapters?.length || 1) * 35)}>
                    <BarChart data={(analysis?.weak_chapters || []).map(w => ({
                      name: w.chapter?.replace(/_/g, ' ').slice(0, 18),
                      accuracy: w.accuracy,
                    }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Bar dataKey="accuracy" radius={[0, 6, 6, 0]}>
                        {(analysis?.weak_chapters || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-[#06D6A0] rounded-2xl mb-4">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-[#06D6A0] mx-auto mb-2" />
                  <p className="font-bold text-[#06D6A0]">No weak areas detected!</p>
                  <p className="text-sm text-slate-500">Keep practicing to maintain your skills</p>
                </CardContent>
              </Card>
            )}

            {/* Prerequisite Gaps */}
            {analysis?.prerequisite_gaps?.length > 0 && (
              <Card className="border-2 border-[#FFD23F] rounded-2xl mb-4">
                <CardContent className="p-4">
                  <button onClick={() => setShowPrereqs(!showPrereqs)} className="w-full flex items-center justify-between">
                    <h3 className="font-bold text-[#FF6B35] text-sm flex items-center gap-1">
                      <Lightbulb className="w-4 h-4" /> Prerequisite Gaps Found
                    </h3>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showPrereqs ? 'rotate-180' : ''}`} />
                  </button>
                  {showPrereqs && (
                    <div className="mt-3 space-y-2">
                      {analysis.prerequisite_gaps.map((g, i) => (
                        <div key={i} className="bg-[#FFF8F0] rounded-xl p-3 border border-[#FFD23F]">
                          <p className="text-xs font-bold text-red-500">Struggling with: {g.weak_concept}</p>
                          <p className="text-xs text-slate-600 mt-1">
                            Needs: <b className="text-[#1B4F8A]">{g.prerequisite}</b>
                            {g.prereq_class && <span className="text-slate-400"> (Class {g.prereq_class})</span>}
                            {g.prereq_accuracy !== null && (
                              <span className={`ml-1 ${g.prereq_accuracy >= 70 ? 'text-[#06D6A0]' : 'text-red-500'}`}>
                                ({g.prereq_accuracy}% accuracy)
                              </span>
                            )}
                            {g.needs_practice && <span className="text-red-500 font-bold ml-1">Practice needed!</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Strong Areas */}
            {analysis?.strong_chapters?.length > 0 && (
              <Card className="border-2 border-[#06D6A0] rounded-2xl mb-4">
                <CardContent className="p-4">
                  <h3 className="font-bold text-[#06D6A0] text-sm mb-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Strong Areas
                  </h3>
                  {analysis.strong_chapters.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex justify-between text-xs font-bold py-1.5">
                      <span className="text-slate-600">{s.chapter?.replace(/_/g, ' ')}</span>
                      <span className="text-[#06D6A0]">{s.accuracy}%</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Start Practice Button */}
            <Button data-testid="start-weakness-practice-btn" onClick={startWeaknessPractice}
              className="w-full bg-[#E63946] hover:bg-[#C5303C] text-white font-extrabold rounded-full py-3 shadow-[0_4px_0_#9B252F] active:shadow-none active:translate-y-[4px]">
              <Target className="w-4 h-4 mr-2" /> Practice Weak Areas Now
            </Button>
          </>
        ) : q && (
          <>
            {/* Question */}
            <div className="mb-4">
              <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                <span>Q{currentIdx + 1}/{questions.length}</span>
                <span className="capitalize text-[#E63946]">{q.chapter?.replace(/_/g,' ')}</span>
              </div>
              <div className="h-2 bg-[#F0E6D8] rounded-full overflow-hidden">
                <div className="h-full bg-[#E63946] rounded-full transition-all" style={{ width: `${((currentIdx+1)/questions.length)*100}%` }} />
              </div>
            </div>
            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-5">
                <div className="text-base font-bold text-[#1B4F8A] mb-4 leading-relaxed">
                  <MathRenderer text={q.question_latex || q.question_text} />
                </div>
                {!feedback && (
                  <>
                    {(q.question_type === 'mcq' || q.question_type === 'assertion_reason' || !q.question_type) && (
                      <div className="space-y-2">
                        {['A','B','C','D'].map(opt => {
                          const t = q[`option_${opt.toLowerCase()}`];
                          if (!t) return null;
                          return (
                            <button key={opt} onClick={() => setAnswer(opt)}
                              className={`w-full text-left p-3 rounded-xl border-2 font-bold text-sm transition-all
                                ${answer === opt ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F0E6D8] text-[#1B4F8A] text-xs font-extrabold mr-2">{opt}</span>
                              <MathRenderer text={t} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {q.question_type === 'true_false' && (
                      <div className="flex gap-3">
                        {['true','false'].map(v => (
                          <button key={v} onClick={() => setAnswer(v)}
                            className={`flex-1 p-3 rounded-xl border-2 font-bold text-sm capitalize ${answer === v ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>{v}</button>
                        ))}
                      </div>
                    )}
                    {['fill_blank','integer_type'].includes(q.question_type) && (
                      <input value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Your answer"
                        className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            {feedback && (
              <Card className={`border-2 rounded-2xl mb-4 ${feedback.is_correct ? 'border-[#06D6A0] bg-[#06D6A0]/5' : 'border-red-300 bg-red-50/50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {feedback.is_correct ? <CheckCircle className="w-5 h-5 text-[#06D6A0]" /> : <XCircle className="w-5 h-5 text-red-500" />}
                    <span className="font-bold text-sm">{feedback.is_correct ? 'Correct!' : `Answer: ${feedback.correct_answer}`}</span>
                  </div>
                  {feedback.solution && <div className="text-sm text-slate-600 mt-2 whitespace-pre-line"><MathRenderer text={feedback.solution} /></div>}
                </CardContent>
              </Card>
            )}
            {!feedback ? (
              <Button onClick={checkAnswer} disabled={!answer} className="w-full bg-[#1B4F8A] text-white font-extrabold rounded-full py-3">Check Answer</Button>
            ) : (
              <Button onClick={nextQ} className="w-full bg-[#E63946] text-white font-extrabold rounded-full py-3">
                {currentIdx >= questions.length - 1 ? 'Finish' : 'Next'}
              </Button>
            )}
          </>
        )}
      </div>
      {!practicing && <BottomNav active="home" />}
    </div>
  );
}
